// Bộ lọc ngay trên cột (filter row của AntGrid): chọn loại editor theo cấu hình cột/field và dịch giá trị người dùng
// nhập sang điều kiện mà BE (AntService.sqlSearchAdvance → AntSqlBuilder.search) hiểu. KHÔNG dùng bộ lọc cục bộ của
// DevExtreme: lưới lấy dữ liệu từ server theo trang.
//
// Loại (kind) → điều kiện:
//   text   → { [field]: { type: 'contain', value } }
//   number → "675" = số CHỨA dãy 675 ở bất kỳ đâu (675 ra 675.000, 1.675.000) → { type: 'contain' };
//            "=420000" = bằng đúng; ">100", ">=", "<", "<=", "<>"/"!=" → { [field]: { type, value } }
//   date   → { [field]_startDate, [field]_endDate } trọn ngày đã chọn (Unix)
//   bool   → { [field]: 1 | 0 }
//   values → { [field]: [mã...] }  (danh mục cố định lookup.values)
//   tags   → { [field]: [giá trị...] } (field mảng — BE tìm dòng chứa ít nhất 1 giá trị)

import AntTime from './AntTime';

const NUMBER_TYPES = ['number', 'currency', 'float', 'int', 'integer', 'bigint', 'decimal', 'percent', 'month', 'year', 'day'];
const DATE_TYPES = ['date', 'datetime', 'timestamp'];
const BOOL_TYPES = ['bool', 'boolean'];
const TEXT_TYPES = ['string', 'phone', 'email', 'text'];
// Không lọc được bằng 1 ô nhập: giờ/phút, mật khẩu, ảnh, html, đối tượng.
const SKIP_TYPES = ['time', 'minute', 'minutes', 'password', 'image', 'html', 'object', 'json', 'file'];
const JOIN_TYPES = ['join', 'first', 'last'];

const fieldNameOf = (f) => (f && typeof f === 'object' ? f.dataField : f);

// spec = { kind, field, items? } | null. getFieldConfig(field) → config field của bảng lưới (null nếu không có — cột
// không có trên bảng thì BE không lọc được → tắt).
// translate(name) → chuỗi hiển thị (name có thể là {vi,en} hoặc danh sách theo điều kiện [{ when, value }]);
// isItemVisible(item) → mục danh mục còn hiện theo setting/quyền (visible/whenSession...).
export const resolveColumnFilterSpec = (col, rawCol, getFieldConfig, translate, isItemVisible) => {
    if (!col || col.isSystemCol || col.command || !col.dataField) return null;
    const raw = rawCol || {};
    if (raw.allowFiltering === false || col.allowFiltering === false) return null;
    const colType = raw.dataType || col.colType;
    const lookupField = (name) => (typeof getFieldConfig === 'function' ? getFieldConfig(name) : null);

    // Cột ghép nhiều field: lọc chữ trên field chữ đầu tiên có trên bảng.
    if (JOIN_TYPES.includes(colType)) {
        const fields = raw.fields instanceof Array ? raw.fields : col.fields instanceof Array ? col.fields : [];
        for (let i = 0; i < fields.length; i++) {
            const name = fieldNameOf(fields[i]);
            const cfg = name ? lookupField(name) : null;
            if (cfg && (!cfg.dataType || TEXT_TYPES.includes(cfg.dataType))) return { kind: 'text', field: name };
        }
        return null;
    }

    const cfg = lookupField(col.dataField);
    if (!cfg) return null;
    const dataType = String(cfg.dataType || raw.dataType || col.dataType || 'string');
    if (SKIP_TYPES.includes(dataType)) return null;

    const lookup = (raw.lookup && raw.lookup.values ? raw.lookup : null) || (cfg.lookup && cfg.lookup.values ? cfg.lookup : null);
    if (lookup && lookup.values instanceof Array && lookup.values.length > 0) {
        const tr = typeof translate === 'function' ? translate : (v) => v;
        const toText = (label, fallback) => {
            const text = label === undefined || label === null ? null : tr(label);
            // Không ra được chuỗi (cấu hình lạ) → dùng mã, không bao giờ hiện "[object Object]".
            return text === undefined || text === null || text === '' || typeof text === 'object' ? String(fallback) : String(text);
        };
        const items = lookup.values
            .map((v) => {
                if (v === null || v === undefined) return null;
                if (typeof v !== 'object') return { value: v, text: String(v) };
                if (typeof isItemVisible === 'function' && !isItemVisible(v)) return null;
                const value = v.code !== undefined ? v.code : v.value !== undefined ? v.value : v.id;
                if (value === undefined || (value !== null && typeof value === 'object')) return null;
                return { value, text: toText(v.name !== undefined ? v.name : v.text, value) };
            })
            .filter(Boolean);
        if (items.length) return { kind: 'values', field: col.dataField, items };
    }

    if (dataType === 'Array') return { kind: 'tags', field: col.dataField };
    if (BOOL_TYPES.includes(dataType) || cfg.editorType === 'checkBox' || /^is_/.test(col.dataField)) return { kind: 'bool', field: col.dataField };
    if (DATE_TYPES.includes(dataType)) return { kind: 'date', field: col.dataField };
    if (NUMBER_TYPES.includes(dataType)) {
        const isPercentFormat = Boolean(col.format && typeof col.format === 'object' && col.format.type === 'percent');
        return { kind: 'number', field: col.dataField, percent: isPercentFormat };
    }
    return { kind: 'text', field: col.dataField };
};

// "1.234.567,5" | "1,234,567.5" | "1234.5" | "1.000" → số; không hợp lệ → NaN.
export const parseLocaleNumber = (text) => {
    let s = String(text === undefined || text === null ? '' : text)
        .replace(/[\s ]/g, '')
        .replace(/[^\d.,+-]/g, '');
    if (!s) return NaN;
    const lastDot = s.lastIndexOf('.');
    const lastComma = s.lastIndexOf(',');
    if (lastDot > -1 && lastComma > -1) {
        const decimal = lastDot > lastComma ? '.' : ',';
        const thousand = decimal === '.' ? ',' : '.';
        s = s.split(thousand).join('').replace(decimal, '.');
    } else if (lastDot > -1 || lastComma > -1) {
        const sep = lastDot > -1 ? '.' : ',';
        const groups = s.split(sep);
        // Nhóm hàng nghìn: mọi nhóm sau có đúng 3 chữ số (1.000 / 1,000,000) → bỏ dấu; ngược lại là dấu thập phân.
        const isThousands = groups.length > 1 && groups.slice(1).every((g) => /^\d{3}$/.test(g)) && /^[+-]?\d{1,3}$/.test(groups[0]);
        s = isThousands ? groups.join('') : groups.length === 2 ? groups.join('.') : NaN;
    }
    if (typeof s !== 'string' || !/^[+-]?\d*\.?\d+$|^[+-]?\d+\.?$/.test(s)) return NaN;
    return Number(s);
};

const NUMBER_OPERATOR = /^\s*(>=|<=|<>|!=|=>|=<|>|<|=)?\s*(.*)$/;

export const parseNumberFilter = (text) => {
    const m = NUMBER_OPERATOR.exec(String(text === undefined || text === null ? '' : text));
    if (!m || !m[2].trim()) return null;
    // Không gõ phép so sánh → số chứa dãy đã gõ ('digits'); '=' gõ rõ ràng → bằng đúng.
    let op = m[1] || 'digits';
    if (op === '<>') op = '!=';
    if (op === '=>') op = '>=';
    if (op === '=<') op = '<=';
    const value = parseLocaleNumber(m[2]);
    if (Number.isNaN(value)) return null;
    return { op, value };
};

// Số → chuỗi như MySQL in ra (không số mũ, không dấu phân cách): 420000, 0.1, -12.5.
const numberToPlainString = (num) => {
    if (!Number.isFinite(num)) return String(num);
    const text = String(num);
    if (!/e/i.test(text)) return text;
    return num.toFixed(20).replace(/\.?0+$/, '');
};

const isBlank = (value) => value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

// Giá trị editor → điều kiện BE (object nhiều khoá). Rỗng / không hợp lệ → {}.
export const buildColumnFilterCondition = (spec, value) => {
    if (!spec || !spec.field || isBlank(value)) return {};
    const field = spec.field;
    switch (spec.kind) {
        case 'text': {
            const text = String(value).trim();
            return text ? { [field]: { type: 'contain', value: text } } : {};
        }
        case 'number': {
            const parsed = parseNumberFilter(value);
            if (!parsed) return {};
            const num = spec.percent ? parsed.value / 100 : parsed.value;
            if (parsed.op === 'digits') return { [field]: { type: 'contain', value: numberToPlainString(num) } };
            return parsed.op === '=' ? { [field]: num } : { [field]: { type: parsed.op, value: num } };
        }
        case 'date': {
            const unix = typeof value === 'number' ? value : value instanceof Date ? Math.floor(value.getTime() / 1000) : NaN;
            if (!Number.isFinite(unix)) return {};
            return { [field + '_startDate']: AntTime.startOfDayFromUnix(unix).unix(), [field + '_endDate']: AntTime.fromUnix(unix).endOf('day').unix() };
        }
        case 'bool':
            return value === 1 || value === true ? { [field]: 1 } : value === 0 || value === false ? { [field]: 0 } : {};
        case 'values':
        case 'tags': {
            const list = (Array.isArray(value) ? value : [value]).filter((v) => !isBlank(v));
            return list.length ? { [field]: list } : {};
        }
        default:
            return {};
    }
};

// Toàn bộ bộ lọc cột: values = { dataField: giá trị editor }, specOf(dataField) → spec.
export const buildColumnFilterConditions = (values, specOf) => {
    const result = {};
    Object.keys(values || {}).forEach((dataField) => {
        Object.assign(result, buildColumnFilterCondition(specOf(dataField), values[dataField]));
    });
    return result;
};

export const hasColumnFilterValues = (values) => Object.keys(values || {}).some((k) => !isBlank(values[k]));
